import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { EntitlementsProvider } from "@/context/EntitlementsContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Signup from "@/pages/Signup";
import InviteAccept from "@/pages/InviteAccept";
import LegalPage from "@/pages/LegalPage";
import SuperAdmin from "@/pages/SuperAdmin";
import Dashboard from "@/pages/Dashboard";
import Vehicles from "@/pages/Vehicles";
import Refuels from "@/pages/Refuels";
import NewRefuel from "@/pages/NewRefuel";
import NewCharging from "@/pages/NewCharging";
import AddHub from "@/pages/AddHub";
import Insights from "@/pages/Insights";
import Reports from "@/pages/Reports";
import VehicleDetail from "@/pages/VehicleDetail";
import Settings from "@/pages/Settings";
import ForgotPassword from "@/pages/ForgotPassword";
import Service from "@/pages/Service";
import FleetDocuments from "@/pages/FleetDocuments";
import LogService from "@/pages/LogService";
import LogRepair from "@/pages/LogRepair";
import UploadBill from "@/pages/UploadBill";
import TutorialCenter from "@/pages/TutorialCenter";
import SubscriptionCheckout from "@/pages/SubscriptionCheckout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Timeline from "@/pages/Timeline";

function NotFoundPage() {
  return (
    <div className="max-w-xl mx-auto text-center py-16" data-testid="not-found-page">
      <p className="ft-overline">404 · PAGE NOT FOUND</p>
      <h1 className="font-display text-5xl font-black tracking-tighter mt-2">Wrong turn.</h1>
      <p className="mt-3 text-sm text-[var(--text-muted)]">
        The page you were looking for doesn't exist. Let's get you back on the road.
      </p>
      <a href="/" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-sm font-semibold" data-testid="not-found-home-btn">
        Go home
      </a>
    </div>
  );
}
import Privacy from "@/pages/Privacy";
import DeleteAccount from "@/pages/DeleteAccount";
import { Toaster } from "@/components/ui/sonner";

function ProtectedShell({ children, adminOnly }) {
  return (
    <ProtectedRoute adminOnly={adminOnly}>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <EntitlementsProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/invite/:token" element={<InviteAccept />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/admin" element={<SuperAdmin />} />
              <Route path="/forgot" element={<ForgotPassword />} />

              {/* Primary 5-tab navigation */}
              <Route path="/" element={<ProtectedShell><Dashboard /></ProtectedShell>} />
              <Route path="/garage" element={<ProtectedShell><Vehicles /></ProtectedShell>} />
              <Route path="/timeline" element={<ProtectedShell><Timeline /></ProtectedShell>} />
              <Route path="/add" element={<ProtectedShell><AddHub /></ProtectedShell>} />
              <Route path="/insights" element={<ProtectedShell><Insights /></ProtectedShell>} />
              <Route path="/profile" element={<ProtectedShell><Settings /></ProtectedShell>} />

              {/* Sub-flows */}
              <Route path="/new" element={<ProtectedShell><NewRefuel /></ProtectedShell>} />
              <Route path="/new/charging" element={<ProtectedShell><NewCharging /></ProtectedShell>} />
              <Route path="/refuels" element={<ProtectedShell><Refuels /></ProtectedShell>} />
              <Route path="/reports" element={<ProtectedShell><Reports /></ProtectedShell>} />
              <Route path="/service" element={<ProtectedShell><Service /></ProtectedShell>} />
              <Route path="/documents" element={<ProtectedShell><FleetDocuments /></ProtectedShell>} />
              <Route path="/log/service"     element={<ProtectedShell><LogService /></ProtectedShell>} />
              <Route path="/log/repair"      element={<ProtectedShell><LogRepair /></ProtectedShell>} />
              <Route path="/log/upload-bill" element={<ProtectedShell><UploadBill /></ProtectedShell>} />
              <Route path="/tutorial"        element={<ProtectedShell><TutorialCenter /></ProtectedShell>} />

              {/* Subscription & payments (v3.1) */}
              <Route path="/subscription/checkout" element={<ProtectedShell><SubscriptionCheckout /></ProtectedShell>} />
              <Route path="/payment/success"       element={<ProtectedShell><PaymentSuccess /></ProtectedShell>} />
              <Route path="/payment/cancel"        element={<ProtectedShell><PaymentCancel /></ProtectedShell>} />

              {/* Vehicle detail (accessible from Garage) */}
              <Route path="/vehicles/:id" element={<ProtectedShell><VehicleDetail /></ProtectedShell>} />

              {/* Legacy aliases (redirect to new nav) */}
              <Route path="/vehicles" element={<Navigate to="/garage" replace />} />
              <Route path="/settings" element={<Navigate to="/profile" replace />} />

              {/* Catch-all: unknown routes go home */}
              <Route path="*" element={<ProtectedShell><NotFoundPage /></ProtectedShell>} />
            </Routes>
          </BrowserRouter>
          <Toaster
            position="top-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "var(--bg-surface, #121212)",
                color: "var(--text-primary, #F5F5F5)",
                border: "1px solid var(--border-line, #262626)",
                borderRadius: 0,
                fontFamily: "'Manrope', sans-serif",
              },
            }}
          />
        </EntitlementsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
